import { Customer } from "@/types/customer";
import { Product } from "@/types/product";
import { PaymentHistory } from "@/types/payment";

export type RealtimeEvent = 
  | { type: 'connected'; sessionId?: string }
  | { type: 'customer_added'; data: Customer }
  | { type: 'customer_updated'; data: Customer }
  | { type: 'customer_deleted'; data: { id: string } }
  | { type: 'product_added'; data: Product }
  | { type: 'product_updated'; data: Product }
  | { type: 'product_deleted'; data: { id: string } }
  | { type: 'jobs_updated'; data: string[] }
  | { type: 'payment_added'; data: PaymentHistory }
  | { type: 'payment_updated'; data: PaymentHistory }
  | { type: 'payment_deleted'; data: { id: string } }
  | { type: 'payment_made'; data: { productId: string; amount: number; newTotal: number } };

export type RealtimeEventHandler = (event: RealtimeEvent) => void;

class RealtimeService {
  private websocket: WebSocket | null = null;
  private handlers: Set<RealtimeEventHandler> = new Set();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectDelay = 2000;
  private isConnected = false;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private isEnabled = true;
  private pingInterval: NodeJS.Timeout | null = null;

  constructor() {
    // Only try to connect if we're in the browser and have the required config
    if (typeof window !== 'undefined') {
      // Delay initial connection to ensure env vars are loaded
      setTimeout(() => this.checkConfigAndConnect(), 500);
    }
  }

  private checkConfigAndConnect() {
    const baseUrl = import.meta.env.VITE_KV_API_URL;
    const token = import.meta.env.VITE_KV_TOKEN;
    
    if (!baseUrl || !token) {
      console.warn('Real-time service: Missing configuration, running in offline mode');
      this.isEnabled = false;
      return;
    }

    this.connect();
  }

  private connect() {
    if (!this.isEnabled || typeof window === 'undefined') return;

    const baseUrl = import.meta.env.VITE_KV_API_URL || '';
    const token = import.meta.env.VITE_KV_TOKEN || '';
    
    try {
      // Convert HTTP URL to WebSocket URL
      const wsUrl = baseUrl.replace('https://', 'wss://').replace('http://', 'ws://');
      const fullUrl = `${wsUrl}/websocket?token=${encodeURIComponent(token)}`;
      
      // Connect to WebSocket endpoint
      this.websocket = new WebSocket(fullUrl);
      
      this.websocket.onopen = () => {
        this.isConnected = true;
        this.reconnectAttempts = 0;
        
        // Clear any pending reconnect timeout
        if (this.reconnectTimeout) {
          clearTimeout(this.reconnectTimeout);
          this.reconnectTimeout = null;
        }
        
        // Start ping interval to keep connection alive
        this.startPingInterval();
      };

      this.websocket.onmessage = (event) => {
        try {
          const realtimeEvent: RealtimeEvent = JSON.parse(event.data);
          
          // Ignore connection messages
          if (realtimeEvent.type === 'connected') {
            return;
          }
          
          this.notifyHandlers(realtimeEvent);
        } catch (error) {
          console.error('Failed to parse real-time event:', error);
        }
      };

      this.websocket.onerror = (error) => {
        console.warn('Real-time WebSocket error:', error);
      };

      this.websocket.onclose = (event) => {
        console.warn('Real-time WebSocket connection closed:', event.code, event.reason);
        this.isConnected = false;
        this.stopPingInterval();
        this.handleReconnect();
      };

    } catch (error) {
      console.warn('Failed to establish real-time WebSocket connection:', error);
      this.handleReconnect();
    }
  }

  private startPingInterval() {
    this.stopPingInterval();
    // Send ping every 30 seconds to keep connection alive
    this.pingInterval = setInterval(() => {
      if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
        this.websocket.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);
  }

  private stopPingInterval() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  private handleReconnect() {
    if (!this.isEnabled) return;
    
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.warn('Max reconnection attempts reached, switching to offline mode');
      this.isEnabled = false;
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
    
    this.reconnectTimeout = setTimeout(() => {
      this.disconnect();
      this.connect();
    }, delay);
  }

  private notifyHandlers(event: RealtimeEvent) {
    this.handlers.forEach(handler => {
      try {
        handler(event);
      } catch (error) {
        console.error('Error in real-time event handler:', error);
      }
    });
  }

  public subscribe(handler: RealtimeEventHandler): () => void {
    this.handlers.add(handler);
    
    // Return unsubscribe function
    return () => {
      this.handlers.delete(handler);
    };
  }

  public disconnect() {
    this.stopPingInterval();
    
    if (this.websocket) {
      this.websocket.close();
      this.websocket = null;
    }
    
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    
    this.isConnected = false;
  }

  public isConnectedToRealtime(): boolean {
    return this.isConnected && this.isEnabled;
  }

  public isRealtimeEnabled(): boolean {
    return this.isEnabled;
  }

  // Emit events (for local testing or fallback)
  public emit(event: RealtimeEvent) {
    this.notifyHandlers(event);
  }

  // Reset connection (for manual retry)
  public reconnect() {
    this.reconnectAttempts = 0;
    this.isEnabled = true;
    this.disconnect();
    this.checkConfigAndConnect();
  }
}

export const realtimeService = new RealtimeService();

// Cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    realtimeService.disconnect();
  });
}